import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import { SectionCard } from './SectionCard';

// Registered once here; every chart in the app uses these elements.
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface TimelinePoint {
  date: string;
  likes: number;
  comments: number;
  shares: number;
  impressions: number;
  posts: number;
  total: number;
}

const formatDay = (dateStr: string) =>
  new Date(dateStr + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

const sharedScales = {
  x: {
    grid: { display: true },
    ticks: { maxRotation: 0, autoSkipPadding: 20, font: { size: 11 } },
  },
  y: {
    beginAtZero: true,
    grid: {},
    ticks: { precision: 0, font: { size: 11 } },
  },
};

const sharedPlugins = (timeline: TimelinePoint[]) => ({
  legend: {
    position: 'bottom' as const,
    labels: { usePointStyle: true, padding: 15, font: { size: 12 } },
  },
  tooltip: {
    padding: 12,
    titleFont: { size: 14 },
    bodyFont: { size: 13 },
    callbacks: {
      afterTitle: (context: { dataIndex: number }[]) => {
        const point = timeline[context[0].dataIndex];
        if (!point) return '';
        return `${point.posts} post${point.posts === 1 ? '' : 's'} published`;
      },
    },
  },
});

/**
 * Engagement broken out by interaction type, as a filled area chart.
 */
export const EngagementOverTime: React.FC<{
  timeline: TimelinePoint[];
  title?: string;
}> = ({ timeline, title = 'Engagement Over Time' }) => {
  const total = timeline.reduce((sum, d) => sum + d.total, 0);

  // Compare the two halves of the range to show direction of travel
  const half = Math.floor(timeline.length / 2);
  const firstHalf = timeline.slice(0, half).reduce((s, d) => s + d.total, 0);
  const secondHalf = timeline.slice(half).reduce((s, d) => s + d.total, 0);
  const change = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : null;

  const data = {
    labels: timeline.map(d => formatDay(d.date)),
    datasets: [
      {
        label: 'Likes',
        data: timeline.map(d => d.likes),
        borderColor: 'rgb(233, 30, 99)',
        backgroundColor: 'rgba(233, 30, 99, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'Comments',
        data: timeline.map(d => d.comments),
        borderColor: 'rgb(25, 118, 210)',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'Shares',
        data: timeline.map(d => d.shares),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderWidth: 2,
        borderDash: [5, 5],
        tension: 0.4,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: sharedPlugins(timeline),
    scales: sharedScales,
  };

  const hasData = timeline.some(d => d.total > 0);

  return (
    <SectionCard title={title}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {total.toLocaleString()}
          </Typography>
          {change !== null && Math.abs(change) >= 0.01 && (
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: change >= 0 ? 'success.main' : 'error.main' }}
            >
              {change >= 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            total interactions over {timeline.length} days
          </Typography>
        </Box>
      </Box>

      <Box>
        <Box sx={{ height: { xs: 220, sm: 320 } }}>
          {hasData ? (
            <Line data={data} options={options} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No engagement recorded for this period
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </SectionCard>
  );
};

/**
 * Posts published per day. Separate from engagement because publishing volume is
 * something you control, and engagement is not.
 */
export const PostingVolume: React.FC<{ timeline: TimelinePoint[] }> = ({ timeline }) => {
  const total = timeline.reduce((sum, d) => sum + d.posts, 0);
  const activeDays = timeline.filter(d => d.posts > 0).length;

  const data = {
    labels: timeline.map(d => formatDay(d.date)),
    datasets: [
      {
        label: 'Posts published',
        data: timeline.map(d => d.posts),
        backgroundColor: 'rgba(25, 118, 210, 0.7)',
        borderRadius: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...sharedPlugins(timeline),
      legend: { display: false },
    },
    scales: sharedScales,
  };

  return (
    <SectionCard title="Publishing Volume">
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>{total.toLocaleString()}</Typography>
          <Typography variant="body2" color="text.secondary">
            posts across {activeDays} active day{activeDays === 1 ? '' : 's'}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Box sx={{ height: { xs: 200, sm: 260 } }}>
          {total > 0 ? (
            <Bar data={data} options={options} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                Nothing published in this period
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </SectionCard>
  );
};

/**
 * Engagement split by platform, stacked so the mix is visible at a glance.
 */
export const PlatformComparison: React.FC<{
  byPlatform: { platform: string; likes: number; comments: number; shares: number; posts: number }[];
  platformName: (id: string) => string;
}> = ({ byPlatform, platformName }) => {
  const data = {
    labels: byPlatform.map(p => platformName(p.platform)),
    datasets: [
      {
        label: 'Likes',
        data: byPlatform.map(p => p.likes),
        backgroundColor: 'rgba(233, 30, 99, 0.8)',
        borderRadius: 3,
      },
      {
        label: 'Comments',
        data: byPlatform.map(p => p.comments),
        backgroundColor: 'rgba(25, 118, 210, 0.8)',
        borderRadius: 3,
      },
      {
        label: 'Shares',
        data: byPlatform.map(p => p.shares),
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderRadius: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: { usePointStyle: true, padding: 15, font: { size: 12 } },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
      },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { font: { size: 11 } } },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0, font: { size: 11 } },
      },
    },
  };

  const hasData = byPlatform.some(p => p.likes + p.comments + p.shares > 0);

  return (
    <SectionCard title="Engagement By Platform">
      <Box>
        <Box sx={{ height: { xs: 220, sm: 280 } }}>
          {hasData ? (
            <Bar data={data} options={options} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No engagement measured yet
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </SectionCard>
  );
};
